import type { Logger } from '@core/model/domain/logger';
import { WebauthJourneyStatus } from '@core/model/domain/webauthJourneyStatus';
import { poll } from '@core/model/framework/notifications/polling';
import { Bad } from '@core/model/framework/Result/BadResult';
import { Ok } from '@core/model/framework/Result/OkResult';
import {
  WaitOnWebauthNotificationGateway,
  WaitOnWebauthNotificationGatewayInput,
  WaitOnWebauthNotificationGatewayOutput,
} from '@core/model/gateway/waitOnWebauthNotificationGateway';
import type { BankService } from '@core/model/service/bankService';
import { SERVICE_ID } from '@di/serviceId';
import { inject, injectable } from 'inversify';
import { BehaviorSubject, Observable, TimeoutError, catchError, firstValueFrom, from, map, of } from 'rxjs';

const PENDING: WebauthJourneyStatus = { status: 'pending', destination: 'widget' };

/*
  Polls the status of the journey the origin page started, by its correlation
  key, until the backend reports how it ended. No snapshot of connections, no
  "new id" detection: the backend writes the outcome on the journey — success
  AND failure — so a bank refusal is seen within one poll, on any device.
*/
@injectable()
export class WaitOnWebauthNotificationViaPolling implements WaitOnWebauthNotificationGateway {
  private manualForceStopPolling$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    @inject(SERVICE_ID.bankService)
    private readonly bankService: BankService,
    @inject(SERVICE_ID.waitOnWebauthTimeoutInSeconds)
    private readonly timeoutInSeconds: number,
    @inject(SERVICE_ID.waitOnWebauthIntervalInSeconds)
    private readonly intervalInSeconds: number,
    @inject(SERVICE_ID.waitOnWebauthInitialDelayInSeconds)
    private readonly initialDelayInSeconds: number,
    @inject(SERVICE_ID.logger)
    private readonly logger: Logger
  ) {}

  async awaitNewBankConnection(
    input: WaitOnWebauthNotificationGatewayInput
  ): WaitOnWebauthNotificationGatewayOutput {
    this.manualForceStopPolling$.next(false);

    try {
      const journeyStatus = await firstValueFrom(
        poll<WaitOnWebauthNotificationGatewayInput, WebauthJourneyStatus>({
          intervalInSeconds: this.intervalInSeconds,
          timeoutInSeconds: this.timeoutInSeconds,
          // The user is still authenticating on the bank page: the first calls
          // would surely come back pending.
          initialDelayInSeconds: this.initialDelayInSeconds,
          apiCall: () => this.getJourneyStatusAsObservable(input),
          shouldContinuePolling: (status) => status.status === 'pending',
          manualForceStop: this.manualForceStopPolling$,
          logger: this.logger,
          pollingName: 'NEW_BANK_CONNECTION',
        })
      );

      if (journeyStatus.status === 'succeeded') {
        return Ok({
          connectionId: journeyStatus.connectionId,
          state: journeyStatus.state,
          destination: journeyStatus.destination,
        });
      }

      // failed: the reason is the backend's business (access_denied, completion_failed…);
      // the page only needs to know it can offer a retry right away.
      this.logger.logTrace(
        `WEBAUTH_JOURNEY: failed (${journeyStatus.status === 'failed' ? journeyStatus.error : 'unknown'})`
      );
      return Bad('WEBAUTH_JOURNEY_FAILED');
    } catch (e) {
      if (e instanceof TimeoutError) {
        return Bad('WAITED_NEW_BANK_CONNECTION_FOR_TOO_LONG');
      } else if (e === 'STOPPED_POLLING_VIA_USER_ACTION') {
        return Bad('STOPPED_POLLING_VIA_USER_ACTION');
      }

      this.logger.logException(e);
      throw e;
    }
  }

  stopWaitingNewBankConnection(): void {
    this.manualForceStopPolling$.next(true);
  }

  /*
    A failed call (network blip, transient 5xx) is not an answer about the
    journey: it counts as pending and the next poll asks again, until the
    overall timeout.
  */
  private getJourneyStatusAsObservable = (
    input: WaitOnWebauthNotificationGatewayInput
  ): Observable<WebauthJourneyStatus> => {
    return from(this.bankService.getWebauthJourneyStatus(input)).pipe(
      map((result) => (result.success === true ? result.value : PENDING)),
      catchError((error) => {
        this.logger.logTrace(`WEBAUTH_JOURNEY: status call failed, keep polling (${String(error)})`);
        return of(PENDING);
      })
    );
  };
}
